import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { DelegationService } from '../../../core/services/delegation-service/delegation.service';
import { constantTxt } from '../../../core/constants/constants';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzTableModule } from 'ng-zorro-antd/table';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-delegation',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    NzDatePickerModule,
    NzButtonModule,
    NzGridModule,
    NzTableModule,
  ],
  templateUrl: './delegation.component.html',
  styleUrl: './delegation.component.less',
})
export class DelegationComponent implements OnInit, OnDestroy {
  delegationForm!: FormGroup;
  delegations: any[] = [];
  loading: boolean = false;
  constants = constantTxt;
  searchDelegationsSubscription!: Subscription;
  deleteDelegationsSubscription!: Subscription;
  pageSize = 100;
  pageNumber = 1;
  customNoDataMessage = constantTxt.noDataFound;

  constructor(
    private readonly fb: FormBuilder,
    private readonly delegationService: DelegationService,
    private readonly router: Router,
    @Inject(PLATFORM_ID) private readonly platformId: Object
  ) {}

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.initializeForm();
      this.searchDelegation();
    }
  }

  initializeForm() {
    this.delegationForm = this.fb.group({
      startDate: [''],
      endDate: [''],
    });
  }

  searchDelegation(type?: any) {
    const { startDate, endDate } = this.delegationForm.value;

    if (new Date(startDate) > new Date(endDate)) {
      alert(
        'Date range is not valid. Your “From” date seems to be later than the “To” date. Please correct it.'
      );
      return;
    }

    const start = new Date(startDate);
    start.setUTCHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setUTCHours(23, 59, 59, 999);
    const payload = {
      startDate: startDate ? start.toISOString() : '',
      endDate: endDate ? end.toISOString() : '',
    };

    if (type == 'submit') {
      if (startDate && endDate) {
        this.loadDelegationData(payload);
      } else {
        Object.values(this.delegationForm.controls).forEach((control: any) => {
          control.setValidators([Validators.required]);
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        });
        alert('Please select period for search !!!');
      }
    } else {
      this.loadDelegationData(payload);
    }
  }

  loadDelegationData(payload: any) {
    this.searchDelegationsSubscription = this.delegationService
      .searchDelegations(payload)
      .subscribe({
        next: (res: any) => {
          let data = res.body;
          if (data) {
            this.delegations = data.map((item: any) => ({
              delId: item.delId,
              delFrom: this.extractName(item.delBy || ''),
              delThrough: item.delThrough
                ? this.extractName(item.delThrough)
                : '',
              delBy: this.extractName(item.delFrom || ''),
              delDateFrom: new Date(item.delDateFrom),
              delDateThrough: new Date(item.delDateThrough),
              selected: false,
            }));
          } else {
            this.delegations = [];
          }
        },
        error: (err: any) => console.error('Search failed', err),
      });
  }

  private extractName(input: string): string {
    let nameOnly = input.replace(/\[.*?\]/g, '').trim();
    if (nameOnly.includes('@')) {
      const [first, last] = nameOnly.split('@')[0].split('.');
      if (!first || !last) return nameOnly;
      return `${this.capitalize(first)} ${this.capitalize(last)}`;
    }

    const nameParts = nameOnly.split(',').map((part) => part.trim());
    if (nameParts.length === 2) {
      return `${this.capitalize(nameParts[1])} ${this.capitalize(
        nameParts[0]
      )}`;
    } else {
      const parts = nameOnly.split(' ');
      if (parts.length === 2) {
        return `${this.capitalize(parts[0])} ${this.capitalize(parts[1])}`;
      }
      return nameOnly;
    }
  }

  private capitalize(word: string): string {
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }

  onDeleteItem(): void {
    const selectedIds = this.delegations
      .filter((d) => d.selected)
      .map((d) => d.delId);

    if (selectedIds.length === 0) {
      alert('Please select at least one delegation to delete.');
      return;
    }

    if (!confirm('Are you sure you want to delete the selected delegations?')) {
      return;
    }

    this.deleteDelegationsSubscription = this.delegationService
      .deleteDelegations(selectedIds)
      .subscribe({
        next: () => {
          alert('Selected delegations deleted successfully.');
          this.searchDelegation(); // Refresh the list
        },
        error: (err) => {
          console.error('Delete error:', err);
          alert('Error deleting delegations.');
        },
      });
  }

  onAddItem(): void {
    this.router.navigate(['/delegation/delegation-add']);
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.pageNumber = 1;
  }

  onPageIndexChange(pageIndex: number): void {
    this.pageNumber = pageIndex;
  }

  ngOnDestroy(): void {
    if (this.searchDelegationsSubscription) {
      this.searchDelegationsSubscription.unsubscribe();
    }
    if (this.deleteDelegationsSubscription) {
      this.deleteDelegationsSubscription.unsubscribe();
    }
  }
}
